import { observer } from 'mobx-react-lite'
import { Platform, StyleSheet, View } from 'react-native'
import { ActionHint } from '@ppwr/poker'
import { translate } from '../../i18n'
import { useStore } from '../../models'
import { BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import { BottomModalContent } from '../../components/ANewDesign/BottomModalContent'
import { DS } from '../../design-system'
import { useThreeCardSize } from './useThreeCardSizes'
import { ReduceMotion } from 'react-native-reanimated'
import { useGameBottomModal } from '../new-design/hooks/useGameBottomModals'

interface PlayerActionsProps {
  actions: ActionHint[]
}

export const ThreeCardPlayerActions = observer(({ actions }: PlayerActionsProps) => {
  const {
    game: {
      minimumRaise,
      callAmount,
      isRaiseAllInForMyPlayer,
      doAllInForPlayer,
      doCallForPlayer,
      doCheckForPlayer,
      doFoldForPlayer,
      hasUserTappedOnAction,
      isGamePaused,
      isMyTurn,
      playerActionTimerExpired,
      showPlayerTimerAfterJoinedGame
    },
    network: { isWebSocketConnected }
  } = useStore()

  const {
    bottomModalContent,
    bottomSheetModalRef,
    snapPoints,
    closeBottomModal,
    handleThreeCardBet,
    //
    enableDismissOnClose,
    renderBackdrop
  } = useGameBottomModal()

  const size = useThreeCardSize()

  const disablePlayerActions =
    hasUserTappedOnAction ||
    !isMyTurn ||
    isGamePaused ||
    !showPlayerTimerAfterJoinedGame ||
    !isWebSocketConnected ||
    playerActionTimerExpired

  if (!minimumRaise) return null

  const raiseIsAllIn = isRaiseAllInForMyPlayer()
  const onPlayerAction = async (action: ActionHint) => {
    switch (action) {
      case ActionHint.CHECK:
        await doCheckForPlayer()
        break
      case ActionHint.FOLD:
        await doFoldForPlayer()
        break
      case ActionHint.ALLIN:
        await doAllInForPlayer()
        break
      case ActionHint.CALL:
        await doCallForPlayer()
        break
      case ActionHint.BET:
      case ActionHint.RAISE:
        handleThreeCardBet()
        break
    }
  }

  const getActionText = (action: ActionHint) => {
    let text = translate(`newGameScreen.threeCardPoker.actionHints.${action}`)
    if (!isMyTurn) return text
    if ([ActionHint.BET, ActionHint.RAISE].includes(action)) {
      return text
    }

    if (action === ActionHint.CALL && callAmount > 0) {
      text = `${text} ${callAmount}`
      return text
    }
    return text
  }

  const showFold = actions.includes(ActionHint.FOLD)
  const betAction = actions.includes(ActionHint.BET)
    ? ActionHint.BET
    : actions.includes(ActionHint.RAISE)
      ? ActionHint.RAISE
      : undefined
  const callAction = actions.includes(ActionHint.CALL)
    ? ActionHint.CALL
    : actions.includes(ActionHint.CHECK)
      ? ActionHint.CHECK
      : undefined
  const allInAction =
    actions.includes(ActionHint.ALLIN) || actions.includes(ActionHint.RAISE) ? ActionHint.ALLIN : undefined
  const isSmall = size === 'small'
  const btnVariant = isSmall ? 'Medium/Primary' : 'Large/Primary'
  return (
    <View style={styles.container}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 8 }}>
        {betAction && !raiseIsAllIn ? (
          <DS.Button
            variant={btnVariant}
            type={'On Light'}
            maxWidth
            style={{ justifyContent: 'center' }}
            disabled={disablePlayerActions}
            rightIcon={<DS.Icon name="Arrow--top-right" color="Global/White" size={16} />}
            onPress={!disablePlayerActions ? () => onPlayerAction(betAction) : undefined}
          >
            {getActionText(betAction)}
          </DS.Button>
        ) : null}

        {allInAction && raiseIsAllIn ? (
          <DS.Button
            variant={btnVariant}
            type={'On Light'}
            maxWidth
            style={{ justifyContent: 'center' }}
            disabled={disablePlayerActions}
            rightIcon={<DS.Icon name="Arrow--top-right" color="Global/White" size={16} />}
            onPress={!disablePlayerActions ? () => onPlayerAction(allInAction) : undefined}
          >
            {getActionText(ActionHint.ALLIN)}
          </DS.Button>
        ) : null}
        {callAction ? (
          <DS.Button
            variant={btnVariant}
            type={'On Dark'}
            maxWidth
            style={{ justifyContent: 'center' }}
            borderColor="Brand/Light Grey"
            disabled={disablePlayerActions}
            rightIcon={<DS.Icon name="Arrow---right" color="Global/Black" size={16} />}
            onPress={!disablePlayerActions ? () => onPlayerAction(callAction) : undefined}
          >
            {getActionText(callAction)}
          </DS.Button>
        ) : null}
      </View>

      {showFold ? (
        <DS.Button
          variant={btnVariant}
          type="On Light"
          borderColor="Brand/Maroon"
          backgroundColor="Brand/Cream"
          textColor="Brand/Maroon"
          disabled={disablePlayerActions}
          style={{ justifyContent: 'center' }}
          centerContent
          rightIcon={<DS.Icon name="Close" color="Brand/Maroon" size={16} />}
          onPress={!disablePlayerActions ? () => onPlayerAction(ActionHint.FOLD) : undefined}
        >
          {getActionText(ActionHint.FOLD)}
        </DS.Button>
      ) : null}
      <BottomSheetModal
        ref={bottomSheetModalRef}
        index={0}
        overrideReduceMotion={ReduceMotion.System}
        snapPoints={snapPoints}
        backdropComponent={renderBackdrop}
        onDismiss={closeBottomModal}
        enableDynamicSizing={false}
        overDragResistanceFactor={3}
        detached={false}
        enableDismissOnClose={enableDismissOnClose}
        enableBlurKeyboardOnGesture={false}
        enableContentPanningGesture={false}
        enableHandlePanningGesture={true}
        enableOverDrag={false}
        enablePanDownToClose={false}
        keyboardBehavior={Platform.OS === 'android' ? 'extend' : 'interactive'}
        keyboardBlurBehavior="none"
        android_keyboardInputMode="adjustResize"
        handleStyle={styles.bottomSheetHandleStyle}
        handleIndicatorStyle={styles.bottomSheetIndicatorStyle}
      >
        <BottomSheetView style={Platform.OS !== 'web' ? { flex: 1 } : {}}>
          <BottomModalContent config={bottomModalContent} />
        </BottomSheetView>
      </BottomSheetModal>
    </View>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 8,
    paddingHorizontal: 20
  },
  bottomSheetHandleStyle: {
    backgroundColor: '#F6F2E9',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 0,
    height: 32
  },
  bottomSheetIndicatorStyle: {
    backgroundColor: 'gray',
    height: 2,
    width: 44
  }
})
